import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CourseProgressCard = ({ course, type = 'current' }) => {
  const navigate = useNavigate();

  const handleContinueLearning = () => {
    navigate('/course-detail-view');
  };

  const handleViewCourse = () => {
    navigate('/course-detail-view');
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-success';
    if (progress >= 50) return 'bg-secondary';
    return 'bg-primary';
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  if (type === 'completed') {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-tier-2 transition-micro">
        <div className="relative h-48 overflow-hidden">
          <Image
            src={course?.thumbnail}
            alt={course?.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute top-4 right-4">
            <div className="bg-success text-success-foreground px-3 py-1 rounded-full text-xs font-medium">
              <Icon name="CheckCircle" size={14} className="inline mr-1" />
              Concluído
            </div>
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="text-white font-heading font-semibold text-lg mb-2">
              {course?.title}
            </h3>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground font-caption">
                Concluído em {course?.completedDate}
              </span>
            </div>
            {course?.certificateAvailable && (
              <div className="flex items-center space-x-1 text-secondary">
                <Icon name="Award" size={16} />
                <span className="text-xs font-medium">Certificado</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-center">
                <p className="text-lg font-heading font-semibold text-foreground">
                  {course?.totalLessons}
                </p>
                <p className="text-xs text-muted-foreground font-caption">Lições</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-heading font-semibold text-foreground">
                  {formatDuration(course?.totalDuration)}
                </p>
                <p className="text-xs text-muted-foreground font-caption">Duração</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleViewCourse}
              iconName="Eye"
              iconPosition="left"
              iconSize={16}
            >
              Ver Curso
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-tier-2 transition-micro">
      <div className="relative h-48 overflow-hidden">
        <Image
          src={course?.thumbnail}
          alt={course?.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute top-4 right-4">
          <div className="bg-background/90 backdrop-blur-sm text-foreground px-3 py-1 rounded-full text-xs font-medium">
            {course?.progress}% concluído
          </div>
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-white font-heading font-semibold text-lg mb-2">
            {course?.title}
          </h3>
          <p className="text-white/80 text-sm font-caption line-clamp-2">
            {course?.description}
          </p>
        </div>
      </div>
      <div className="p-6">
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Progresso</span>
            <span className="text-sm text-muted-foreground font-caption">
              {course?.progress}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${getProgressColor(course?.progress)}`}
              style={{ width: `${course?.progress}%` }}
            />
          </div>
        </div>

        {/* Next Lesson Info */}
        {course?.nextLesson && <div className="mb-4 p-3 bg-background rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-1">
                Próxima Lição:
              </p>
              <p className="text-sm text-muted-foreground font-caption">
                {course?.nextLesson}
              </p>
            </div>
          </div>
        </div>}

        {/* Course Stats */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            {Number.isFinite(course?.timeSpent) && <div className="flex items-center space-x-1 text-muted-foreground">
              <Icon name="BookOpen" size={16} />
              <span className="text-sm font-caption">
                {course?.completedLessons}/{course?.totalLessons} lições
              </span>
            </div>}
            <div className="flex items-center space-x-1 text-muted-foreground">
              <Icon name="Clock" size={16} />
              <span className="text-sm font-caption">
                {formatDuration(course?.timeSpent)} estudadas
              </span>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <Button
          variant="default"
          fullWidth
          onClick={handleContinueLearning}
          iconName="Play"
          iconPosition="left"
          iconSize={16}
        >
          Continuar Aprendendo
        </Button>
      </div>
    </div>
  );
};

export default CourseProgressCard;
